import find from 'lodash/find';
import { cast, flow, getSnapshot, Instance, types } from 'mobx-state-tree';
import { splitPhone } from 'utils';
import { IDepartment, IPhone, IPosition, IPositionInfo, IWorker } from 'types';
import { Providers, Tenants, Workers } from 'http-requests';
import { ISelectValue } from 'components/ui';

import { PhoneModel, RequestStateModel } from './general-models';

export interface IWorkerModel extends Instance<typeof WorkerModel> {}

const RequestsStatuses = types.model({
  getDepartmentsPublic: types.optional(RequestStateModel, {}),
  workers: types.optional(RequestStateModel, {}),
  getDocsProviders: types.optional(RequestStateModel, {}),
  getDocsDepartments: types.optional(RequestStateModel, {}),
});

const DocProviderModel = types
  .model({
    name: types.optional(types.string, ''),
    _id: types.optional(types.string, ''),
  })
  .views((self) => ({
    get providerSelectValue(): ISelectValue {
      return {
        label: self.name,
        value: self._id,
      };
    },
  }));

export const WorkerModel = types
  .model('WorkerModel', {
    id: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), ['Worker']),
    inn: types.optional(types.maybeNull(types.string), null),
    email: types.optional(types.maybeNull(types.string), null),
    phones: types.optional(types.array(PhoneModel), []),
    short_name: types.optional(types.string, ''),
    str_name: types.optional(types.string, ''),
    number: types.optional(types.string, ''),
    position: types.optional(types.frozen(), {}),
    department: types.optional(types.frozen(), {}),
    settings: types.optional(types.frozen(), {}),
    has_access: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
  }))
  .actions((self) => ({
    addPhone: flow(function* (newPhone: string) {
      const { code, number } = splitPhone(newPhone);
      const proccessedPhone = PhoneModel.create({
        code,
        number,
        str_number: newPhone,
      });

      try {
        const response = yield Tenants.SetTenantContacts(self.id, {
          phones: [...self.phones, proccessedPhone],
        });
        self.phones.replace(response.phones);

        return response.phones;
      } catch (error) {
        console.error(error);
      }
    }),
    setPhones(phones: IPhone[]) {
      self.phones = cast(phones);
    },
  }));

export const WorkersStore = types
  .model({
    statuses: types.optional(RequestsStatuses, {}),
    departments: types.optional(types.array(types.frozen<IDepartment>()), []),
    workersList: types.optional(types.array(WorkerModel), []),
    docsProviders: types.optional(types.array(DocProviderModel), []),
    docsDepartments: types.optional(
      types.array(types.frozen<IDepartment>()),
      []
    ),
  })
  .views((self) => ({
    get docsProvidersSelectValue() {
      return self.docsProviders.map((item) => item.providerSelectValue);
    },
    get readyToLoadDepartmentsPublic() {
      return (
        !self.statuses.getDepartmentsPublic.pending &&
        !self.departments.length &&
        !self.statuses.getDepartmentsPublic.fulfilled
      );
    },
    get readyToLoadDocsDepartmentsAndProviders() {
      return (
        !self.statuses.getDocsProviders.pending &&
        !self.statuses.getDocsDepartments.pending &&
        !self.statuses.getDocsProviders.fulfilled &&
        !self.statuses.getDocsDepartments.fulfilled
      );
    },
    get allPositionNames(): IPositionInfo[] {
      const result: IPositionInfo[] = [];
      self.departments.forEach((dep) => {
        result.push(
          ...dep.positions.map((pos) => ({
            id: pos.id,
            name: pos.name,
            dep_id: dep.id,
            dep_name: dep.name,
          }))
        );
      });

      return result;
    },
    getDepartmentName(depId: string) {
      return find(self.departments, { id: depId })?.name || '';
    },
    get departmentOptionsWithTenantTicketsSetting() {
      return self.departments
        .filter((department) => department.settings?.tenant_tickets)
        .map((department) => ({
          label: department.name,
          value: department.id,
        }));
    },
    getCurrentPositions(id: string, anotherProvider?: boolean) {
      const department = find(
        anotherProvider ? self.docsDepartments : self.departments,
        { id }
      );

      return department?.positions || [];
    },
    getDepartmentWorkers(depId: string, withDismissed?: boolean) {
      const department = find(self.departments, { id: depId });
      const all: IWorker[] = [];

      if (department?.positions) {
        department.positions.forEach((pos) => {
          pos.workers.forEach((wrk) => {
            if (withDismissed) {
              all.push({
                ...wrk,
                department: {
                  id: department.id,
                  name: department.name,
                },
                position: pos,
              });
            } else {
              if (!wrk.is_dismiss) {
                all.push({
                  ...wrk,
                  department: {
                    id: department.id,
                    name: department.name,
                  },
                  position: pos,
                });
              }
            }
          });
        });
      }

      return all;
    },
    getCurrentWorkers(
      depId: string,
      posId: string,
      withDismissed?: boolean,
      anotherProvider?: boolean
    ): IWorker[] {
      const department = find(
        anotherProvider ? self.docsDepartments : self.departments,
        { id: depId }
      );
      const position = find(department?.positions, { id: posId });

      if (!department || !position) {
        return [];
      }

      if (withDismissed) {
        return (
          position?.workers.map((wrk) => ({
            ...wrk,
            department: {
              id: department?.id,
              name: department?.name,
            },
            position,
          })) || []
        );
      }

      return (
        position?.workers
          .filter((worker) => !worker.is_dismiss)
          .map((wrk) => ({
            ...wrk,
            department: {
              id: department?.id,
              name: department?.name,
            },
            position,
          })) || []
      );
    },
    getAllWorkers(withDismissed?: boolean) {
      let workers: any[] = [];
      self.departments.forEach((dep: IDepartment) => {
        dep.positions.forEach((pos: IPosition) => {
          if (pos.workers && pos.workers.length) {
            pos.workers.forEach((worker: IWorker) => {
              workers.push({
                id: worker.id,
                str_name: worker.str_name,
                short_name:
                  worker.str_name + (worker.is_dismiss ? ' (Не работает)' : ''),
                name: worker.short_name,
                position: {
                  _id: pos.id,
                  name: pos.name,
                  code: pos.code,
                },
                has_access: worker.has_access,
                department: {
                  id: dep.id,
                  name: dep.name,
                },
                phones: worker.phones,
                email: worker.email,
                is_dismiss: worker.is_dismiss,
                settings: worker.settings,
              });
            });
          }
        });
      });

      if (withDismissed) {
        return workers;
      }

      workers = workers.filter((worker) => !worker.is_dismiss);

      return workers;
    },
    getAllWorkersCount(withDismiss?: boolean): number {
      return self.departments.reduce(
        (departmentAcc, department) =>
          departmentAcc +
          department.positions.reduce(
            (positionsAcc, position) =>
              positionsAcc +
              position.workers.filter((worker) =>
                withDismiss ? worker : !worker.is_dismiss
              ).length,
            0
          ),
        0
      );
    },
    getByName(str: string, withDismissed?: boolean) {
      const result: IWorker[] = [];
      let allWorkers: IWorker[] = [];

      self.departments.forEach((dep: IDepartment) => {
        dep.positions.forEach((pos: IPosition) => {
          if (pos.workers && pos.workers.length) {
            pos.workers.forEach((worker: IWorker) => {
              allWorkers.push({
                ...worker,
                department: dep,
                position: pos,
                str_name:
                  worker.str_name + (worker.is_dismiss ? ' (Не работает)' : ''),
              });
            });
          }
        });
      });

      if (!withDismissed) {
        allWorkers = allWorkers.filter((worker) => !worker.is_dismiss);
      }

      allWorkers.forEach((worker) => {
        const index = worker.short_name
          .toLowerCase()
          .indexOf(str.toLowerCase());

        if (index !== -1) {
          result.push(worker);
        }
      });

      return result;
    },
    getById(id: string) {
      const workers: any[] = [];
      self.departments.forEach((dep: IDepartment) => {
        dep.positions.forEach((pos: IPosition) => {
          if (pos.workers && pos.workers.length) {
            pos.workers.forEach((worker: IWorker) => {
              workers.push({
                id: worker.id,
                short_name:
                  worker.str_name + (worker.is_dismiss ? ' (Не работает)' : ''), //TODO: short это str, вышла путаница!
                name: worker.short_name,
                position: {
                  _id: pos.id,
                  name: pos.name,
                  code: pos.code,
                },
                has_access: worker.has_access,
                department: {
                  id: dep.id,
                  name: dep.name,
                },
                phones: worker.phones,
                is_dismiss: worker.is_dismiss,
                settings: worker.settings,
                email: worker.email,
              });
            });
          }
        });
      });
      const worker = find(workers, { id: id });

      return worker || {};
    },
  }))
  .actions((self) => ({
    setWorkersList(newList: Array<IWorkerModel>) {
      self.workersList.replace(newList);
    },
    getDepartmentsPublic: flow(function* () {
      try {
        self.statuses.getDepartmentsPublic.loadingStates();

        self.departments = yield Workers.GetDepartmentsPublic();

        self.statuses.getDepartmentsPublic.successStates();

        return self.departments;
      } catch (e) {
        self.statuses.getDepartmentsPublic.errorStates();
        console.error(e);
      } finally {
        self.statuses.getDepartmentsPublic.finallyStates();
      }
    }),
    getDocsDepartments: flow(function* () {
      try {
        self.statuses.getDocsDepartments.loadingStates();

        const response = yield Workers.GetDocsDepartments();
        self.docsDepartments = response;
        self.statuses.getDocsDepartments.successStates();

        return response;
      } catch (e) {
        self.statuses.getDocsDepartments.errorStates();
        console.error(e);
      } finally {
        self.statuses.getDocsDepartments.finallyStates();
      }
    }),
    getDocsProviders: flow(function* () {
      try {
        self.statuses.getDocsProviders.loadingStates();
        const response = yield Providers.GetDocsProviders();
        self.docsProviders = response.providers;
        self.statuses.getDocsProviders.successStates();

        return response.providers;
      } catch (e) {
        self.statuses.getDocsProviders.errorStates();
        console.error(e);
      } finally {
        self.statuses.getDocsProviders.finallyStates();
      }
    }),
    getWorkersByPhone: flow(function* (value: string) {
      const phone = splitPhone(value);

      try {
        self.statuses.workers.loadingStates();
        const response = yield Workers.GetWorkersCardsByPhone({
          phones__str_number: phone.code + phone.number,
          is_dismiss__ne: true,
        });
        self.workersList = response.results;
        self.statuses.workers.successStates();

        return self.workersList;
      } catch (error) {
        self.statuses.workers.errorStates();

        throw error;
      } finally {
        self.statuses.workers.finallyStates();
      }
    }),
    changeTicketsAccess: flow(function* (
      worker: IWorker,
      tickets_access_level: 'basic' | 'by_dept' | 'all'
    ) {
      try {
        const response = yield Workers.ChangeTicketsAccess(worker.id, {
          settings: { tickets_access_level },
        });

        worker.settings.tickets_access_level =
          response.settings.tickets_access_level;

        return response;
      } catch (error) {
        throw error;
      }
    }),
  }))
  .actions((self) => ({
    invalidateDepartmentsPublic() {
      self.getDepartmentsPublic();
    },
  }));
